import { useContext, useEffect, useMemo, useRef } from 'react';
import useData from '../../../Utilities/Hooks/UseData';
import { CounterBadge } from '../../../Utilities/CounterBadge';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SessionContext } from '../../../Contexts/SessionContext';
import TripRateParamerterDescriptionViewModel from '../../ViewModels/TripRateParamerterDescriptionViewModel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import HelpLink from '../../../Popups/HelpLink';
import { useTripRateContext } from "../../../TripRate/TripRateContext";
import { getFilteredSurveysForSession } from '../../../TripRateNew/TriprateFilters';
import TripRateParameterOptionViewModel from '../../ViewModels/TripRateParameterOptionViewModel';

const defaultTripRateParameterNames = new Set([
    'no of dwellings',
    'number of dwellings',
]);

const isDefaultTripRateParameter = (name: string) => {
    const normalizedName = name
        .trim()
        .toLowerCase()
        .replace(/\./g, '')
        .replace(/\s+/g, ' ');

    return defaultTripRateParameterNames.has(normalizedName);
};

export default function TripRateParameters() {
    const { surveys } = useTripRateContext();
    const { sessionState, setSessionField } = useContext(SessionContext);
    const appliedDefaultKey = useRef<string | null>(null);
    const previousLandUseKey = useRef<string | null>(null);
    const { data } = useData<TripRateParameterOptionViewModel[]>(
        'TripRateFilterNew',
        `GetParamOptions?landUse=${sessionState.tripRateMain}&subuse=${sessionState.tripRateSub}&v=2`
    );

    const filteredServiceSurveys = useMemo(
        () => getFilteredSurveysForSession(surveys, sessionState, { ignoreHeaderIndex: true }),
        [surveys, sessionState]
    );

    const tripRateParameters = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0 || !Array.isArray(filteredServiceSurveys) || filteredServiceSurveys.length === 0) {
            return [];
        }

        const keyCounts: Record<string, number> = { param1: 0, param2: 0, param3: 0, param4: 0 };

        for (const survey of filteredServiceSurveys) {
            if (survey.param1 != null) keyCounts.param1++;
            if (survey.param2 != null) keyCounts.param2++;
            if (survey.param3 != null) keyCounts.param3++;
            if (survey.param4 != null) keyCounts.param4++;
        }

        return data.slice(0, 4).map((param, index) => {
            const safeName = param?.name ?? '';
            const key = `param${index + 1}` as keyof typeof keyCounts;
            const isParking = safeName.toLowerCase().includes('parking');
            const count = isParking ? filteredServiceSurveys.length : (keyCounts[key] ?? 0);

            return {
                name: safeName,
                unit: param?.unit ?? '',
                count: count.toLocaleString(),
                disabled: count === 0,
            } as TripRateParamerterDescriptionViewModel;
        });
    }, [data, filteredServiceSurveys]);

    useEffect(() => {
        const selectedParam = tripRateParameters[sessionState.primaryFilters.headerIndex ?? 0];
        if (!selectedParam) return;

        setSessionField('tripRateParamName', selectedParam.name);
        setSessionField('tripRateParamUnit', selectedParam.unit);
    }, [tripRateParameters, sessionState.primaryFilters.headerIndex, setSessionField]);

    useEffect(() => {
        const landUseKey = `${sessionState.tripRateMain}|${sessionState.tripRateSub}`;
        if (previousLandUseKey.current !== landUseKey) {
            appliedDefaultKey.current = null;
            previousLandUseKey.current = landUseKey;
        }

        const defaultIndex = tripRateParameters.findIndex(param =>
            isDefaultTripRateParameter(param.name) && !param.disabled
        );
        if (defaultIndex < 0) return;

        const parameterKey = tripRateParameters.map(param => param.name).join('|');
        const defaultKey = `${landUseKey}|${parameterKey}`;
        if (appliedDefaultKey.current === defaultKey) return;

        appliedDefaultKey.current = defaultKey;

        const selectedParam = tripRateParameters[defaultIndex];
        setSessionField('primaryFilters.headerIndex', defaultIndex);
        setSessionField('tripRateParamName', selectedParam.name);
        setSessionField('tripRateParamUnit', selectedParam.unit);
    }, [tripRateParameters, sessionState.tripRateMain, sessionState.tripRateSub, setSessionField]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newIndex = parseInt(event.target.value, 10);
        const selectedParam = tripRateParameters[newIndex];
        if (!selectedParam) return;

        setSessionField('primaryFilters.headerIndex', newIndex);
        setSessionField('tripRateParamName', selectedParam.name);
        setSessionField('tripRateParamUnit', selectedParam.unit);
    };

    return (
        <Grid container component={Paper} size={12} sx={{ position: 'relative' }}>
            <CounterBadge value={1} />
            <Grid size={12}>
                <Typography variant="h6" gutterBottom textAlign='center'>
                    Trip Rate Parameters
                </Typography>
            </Grid>

            {tripRateParameters.length > 0 && (
                <>
                    <Grid size={11}>
                        <RadioGroup
                            value={sessionState.primaryFilters.headerIndex?.toString() ?? '0'}
                            onChange={handleChange}
                        >
                            {tripRateParameters.map((param, index) => (
                                param.name.length > 0 &&
                                <FormControlLabel
                                    key={index}
                                    value={index.toString()}
                                    control={<Radio />}
                                    disabled={param.disabled}
                                    label={`${param.name} ${param.count}`}
                                />
                            ))}
                        </RadioGroup>
                    </Grid>
                    <Grid size={1}>
                        <HelpLink page="Definitions of trip rate parameters" />
                    </Grid>
                </>
            )}
        </Grid>
    );
}
